import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Send, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SocialLinks } from "@/components/SocialLinks";
import { toast } from "sonner";
import logo from "@/assets/logo.png";
import SEO from "@/components/SEO";

const Contact = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [inquiryType, setInquiryType] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !inquiryType || !message.trim()) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSending(true);
    // Simulate send — replace with edge function call when ready
    await new Promise((r) => setTimeout(r, 1200));
    toast.success("Message sent! We'll get back to you soon.");
    setName("");
    setEmail("");
    setInquiryType("");
    setMessage("");
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Contact GreyRock AI® — Support, Creators & Legal"
        description="Reach the GreyRock AI team for product support, creator partnerships, press, or legal inquiries. Messages are routed to the right channel within one business day."
        path="/contact"
      />
      <div className="fixed inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, hsl(0 0% 7%) 0%, hsl(0 0% 4%) 100%)" }} />

      {/* Nav */}
      <nav className="relative z-20 flex items-center justify-between px-6 py-4 md:px-12 border-b border-glass-border bg-background/60 backdrop-blur-xl">
        <Link to="/" className="flex items-center gap-3">
          <img src={logo} alt="GreyRock AI" className="w-10 h-10 object-contain drop-shadow-[0_0_12px_rgba(0,242,255,0.3)]" />
          <span className="text-xl font-bold tracking-tight">
            <span className="text-foreground">Grey</span>
            <span className="text-cyan">Rock</span>
            <span className="text-foreground"> AI</span>
            <sup className="text-[10px] text-muted-foreground">&reg;</sup>
          </span>
        </Link>
        <Link to="/" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
      </nav>

      <div className="relative z-10 container mx-auto px-6 py-12 max-w-xl space-y-10">
        {/* Header */}
        <div className="text-center space-y-3">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
            <span className="text-foreground">Contact </span>
            <span className="text-gradient-cyan">Us</span>
          </h1>
          <p className="text-sm text-muted-foreground">We&rsquo;d love to hear from you</p>
        </div>

        {/* Contact Form */}
        <form onSubmit={handleSubmit} className="glass-card p-6 md:p-8 space-y-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 rounded-xl bg-cyan/10">
              <Mail className="w-6 h-6 text-cyan" />
            </div>
            <h2 className="font-semibold text-lg text-foreground">Send a Message</h2>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name" className="text-foreground">Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              maxLength={100}
              className="bg-secondary border-glass-border"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-foreground">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              maxLength={255}
              className="bg-secondary border-glass-border"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="inquiry-type" className="text-foreground">Inquiry Type</Label>
            <Select value={inquiryType} onValueChange={setInquiryType}>
              <SelectTrigger id="inquiry-type" aria-label="Inquiry Type" className="bg-secondary border-glass-border">
                <SelectValue placeholder="Select inquiry type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="support">Support</SelectItem>
                <SelectItem value="creator-partnerships">Creator Partnerships</SelectItem>
                <SelectItem value="media">Media / Legal</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="text-foreground">Message</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="How can we help?"
              rows={5}
              maxLength={2000}
              className="bg-secondary border-glass-border resize-none"
            />
          </div>

          <Button
            type="submit"
            disabled={sending}
            className="w-full bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground font-semibold hover:shadow-[0_0_30px_-5px_hsl(184_100%_50%_/_0.4)] transition-all"
          >
            {sending ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                Sending...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Send className="w-4 h-4" /> Send Message
              </span>
            )}
          </Button>
        </form>

        {/* Direct Contact */}
        <div className="text-center space-y-2">
          <p className="text-sm text-muted-foreground">
            Or email us directly at{" "}
            <a href="mailto:support@greyrockai.app" className="text-cyan hover:underline">
              support@greyrockai.app
            </a>
          </p>
          <SocialLinks className="justify-center" />
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 border-t border-glass-border py-8 px-6 text-center space-y-3">
        <div className="flex items-center justify-center gap-2 mb-3">
          <img src={logo} alt="GreyRock AI" className="w-6 h-6 object-contain opacity-60" />
          <span className="text-sm font-medium text-muted-foreground">GreyRockAI.app&reg;</span>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-4 text-xs">
          <Link to="/about" className="text-muted-foreground hover:text-cyan transition-colors">About</Link>
          <span className="text-muted-foreground/40">&bull;</span>
          <Link to="/contact" className="text-muted-foreground hover:text-cyan transition-colors">Contact</Link>
          <span className="text-muted-foreground/40">&bull;</span>
          <Link to="/privacy" className="text-muted-foreground hover:text-cyan transition-colors">Privacy Policy</Link>
          <span className="text-muted-foreground/40">&bull;</span>
          <Link to="/terms" className="text-muted-foreground hover:text-cyan transition-colors">Terms of Service</Link>
          <span className="text-muted-foreground/40">&bull;</span>
          <Link to="/refunds" className="text-muted-foreground hover:text-cyan transition-colors">Refund Policy</Link>
        </div>
        <p className="text-sm text-muted-foreground pt-2">&copy; 2026 GreyRock AI&reg;. All Rights Reserved.</p>
      </footer>
    </div>
  );
};

export default Contact;
